import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@clerk/clerk-react';
import {
  subscriptionClient,
  type UpdateSubscriptionResponse,
} from '@/api/subscription/subscriptionClient';

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message: string;
}

export function useUpdateSubscription() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { newPlanId: string }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      return subscriptionClient.updateSubscription({
        userId,
        newPlanId: data.newPlanId,
      });
    },
    onSuccess: (response: UpdateSubscriptionResponse) => {
      // Issue #3308 H-2 (PR-12) + Issue #3309 PR-14 統合:
      // - subscription/status: usePlanOverageStatus が即時 refetch して banner 表示判定 (downgrade で overage 解消、 upgrade で limit 拡大)
      // - customers / staff: page filter / count が新 plan 上限と整合
      // upgrade/downgrade 双方で実行 (PR-12 の冒頭 invalidate を継承)
      queryClient.invalidateQueries({ queryKey: ['subscription', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });

      if (response.checkoutUrl) {
        // Upgrade: Stripe Checkout URLへリダイレクト
        window.location.href = response.checkoutUrl;
        return;
      }

      if (response.scheduledChangeAt) {
        // Downgrade (Issue #3309 PR-14): 期間末 schedule 完了
        const date = new Date(response.scheduledChangeAt);
        toast.success('プラン変更を予約しました', {
          description: `${date.toLocaleDateString('ja-JP')} から ${response.subscription.newPlan} に切り替わります`,
        });
        queryClient.invalidateQueries({ queryKey: ['billing'] });
        return;
      }

      console.error(
        '[useUpdateSubscription] unexpected response shape',
        response
      );
      toast.error('プラン変更レスポンスが不正です');
    },
    onError: (error: Error | ApiError) => {
      console.error('Subscription update failed:', error);
      const errorMessage =
        'response' in error && error.response?.data?.message
          ? error.response.data.message
          : error.message;

      toast.error('プラン変更に失敗しました', {
        description: errorMessage,
      });
    },
  });
}
